<script>
import utils from '@/common/utils';
import { defineComponent, onMounted, computed, ref, watch, inject } from 'vue'
import CcAnchorModal from '@/components/levelling/controlCentre/CcAnchorModal.vue'
import { anchorDraftPill } from '@rm-ui/levelling/levellingMeta'
import { isAtlasProvenance, provenanceLabel } from '@rm-ui/levelling/rationaleDraft'

export default defineComponent({
  name: 'EvaluationSummary',
  components: { CcAnchorModal },
  props: {
    evaluation: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    levels: {
      type: Array,
      required: true,
    },
    answers: {
      type: Array,
      required: true
    },
    factors: {
      type: Array,
      required: true
    },
    functionTrackLevels: {
      type: Array,
      required: false
    },
    tracks: {
      type: Array,
      default: () => []
    },
    functions: {
      type: Array,
      default: () => []
    },
    /* False when the framework leaves no track choice to make — a single
       discipline levelling a single career track. The evaluation still HAS a
       track; there is just nothing to send the user back to change. */
    canEditTrack: {
      type: Boolean,
      default: true
    },
    selectedTrack: {
      type: Object,
      required: true
    },
    selectedFunction: {
      type: Object,
      required: true
    },
    classificationAnswers: {
      type: Array,
      default: () => []
    },
    /* ---- Anchor roles (PD-1258) ----
       `canAnchor`: feature on AND the user is in levelling_anchor_roles_admin.
       `ceiling`: the client's CEO ceiling ({ doc_id, title, levelCode,
       levelPosition }) or null — used only for the ADVISORY "at or above the
       ceiling" flag. It never blocks saving. */
    canAnchor: {
      type: Boolean,
      default: false
    },
    /* The markRaw'd levellingAnchors() self object, for the record-level
       governance bar (set / remove an anchor after the evaluation completes). */
    anchors: {
      type: Object,
      default: null
    },
    docName: {
      type: String,
      default: null
    },
    ceiling: {
      type: Object,
      default: null
    },
    docId: {
      type: [Number, String],
      default: null
    },
  },
  emits: ['restart', 'evaluateToAnchor', 'delete', 'save', 'complete', 'compare', 'editSubFactor', 'editClassificationFactor', 'editTrack', 'changeTrack', 'changeFunction', 'reviewAnswers'],
  setup(props, { emit }) {
    const loginState = inject('loginState')
    const isClassification = computed(() => props.evaluation.flowType === 'classification')
    const isDraft = computed(() => props.evaluation.status === 'DRAFT')
    // Only the standard flow drives answers straight from the SubFactorQuestion
    // page, so only there can a sub-factor answer be edited in place. Guided and
    // classification flows derive these levels and would lose the edit on reload.
    const isStandardFlow = computed(() => props.evaluation.flowType === 'standard')
    const selectedLevel = ref(null)
    const saveAttempted = ref(false)
    const comments = ref('')
    const editingComments = ref(false)
    // Who last saved the evaluation (and when) — surfaced alongside the rationale,
    // matching the factor/sub-factor questions. Falls back to the current user.
    const updatedBy = computed(() => props.evaluation.updated_by ?? loginState.user.name)
    const updatedDate = computed(() => props.evaluation.comments ? props.evaluation.updated_date : null)
    const expandedFactors = ref({})

    function toggleFactorExpanded(key) {
      expandedFactors.value = { ...expandedFactors.value, [key]: !expandedFactors.value[key] }
    }

    // Factor row keys and sub-factor justification keys, used by expand/collapse-all.
    function factorKeys() {
      return groupedAnswers.value.map((group, gi) => `factor${gi}`)
    }

    function commentKeys() {
      const keys = []
      groupedAnswers.value.forEach((group, gi) => {
        group.answers.forEach((answer, ci) => {
          if (answer.comments) keys.push(`factor${gi}-sf${ci}`)
        })
      })
      return keys
    }

    // Cycles: collapsed -> factors expanded -> factors + comments expanded -> collapsed.
    const expandAllStage = computed(() => {
      const factorsOpen = factorKeys().every(key => expandedFactors.value[key])
      const commentsOpen = commentKeys().every(key => expandedFactors.value[key])
      if (factorsOpen && commentsOpen) return 'all'
      if (factorsOpen) return 'factors'
      return 'collapsed'
    })

    function cycleExpandAll() {
      const next = {}
      if (expandAllStage.value === 'collapsed') {
        // expand factors only
        factorKeys().forEach(key => { next[key] = true })
      }
      // else if (expandAllStage.value === 'factors') {
      //   // expand factors + comments
      //   factorKeys().forEach(key => { next[key] = true })
      //   commentKeys().forEach(key => { next[key] = true })
      // }
      // 'all' -> leave everything collapsed (empty object)
      expandedFactors.value = next
    }

    function groupHasComments(group) {
      return group.answers.some(a => a.comments)
    }

    function groupSubFactorItems(group) {
      return group.answers.map(a => ({
        factorId: a.factorId,
        subFactorId: a.subFactorId,
        label: a.subFactorName,
        text: a.comments,
        provenance: a.rationaleProvenance || null,
        levelScore: Math.round(getSelectedLevelScore(a)),
        levelText: getSelectedLevelText(a),
        levelColor: getLevelColor(a.selectedLevel),
        score: getSubFactorScore(a),
      }))
    }
    onMounted(async () => {
      const savedSuggestedLevel = Number(props.levels.find(l => l.id === props.evaluation.suggestedLevelId)?.name)
      const savedScore = props.evaluation.score

      if (suggestedLevel.value !== savedSuggestedLevel) {
        // The recommended level has shifted since it was last saved (e.g. the
        // track was changed). Reset any manual adjustment so the selected level
        // tracks the new suggestion, and persist both.
        selectedLevel.value = suggestedLevel.value
        comments.value = ''
      } else {
        selectedLevel.value = Number(props.levels.find(l => l.id === props.evaluation.selectedLevelId)?.name) || suggestedLevel.value
        comments.value = props.evaluation.comments || ''
      }

      // Open the editor only when a level adjustment is missing its (required)
      // justification. Otherwise any existing comment is shown read-only, and an
      // absent one is offered via the "Add rationale" button.
      editingComments.value = selectedLevel.value !== suggestedLevel.value && !comments.value

      if ((suggestedLevel.value && suggestedLevel.value !== savedSuggestedLevel)
        || (totalScore.value && savedScore !== totalScore.value))
        save()
    })

    const isDirty = computed(() => {
      const savedSelectedLevel = Number(props.levels.find(l => l.id === props.evaluation.selectedLevelId)?.name)
      const savedComments = props.evaluation.comments || ''
      return selectedLevel.value !== savedSelectedLevel || comments.value !== savedComments
    })

    function save() {
      emit('save', {
        score: totalScore.value,
        suggestedLevelId: props.levels.find(l => l.name == suggestedLevel.value)?.id ?? null,
        selectedLevelId: props.levels.find(l => l.name == selectedLevel.value)?.id ?? null,
        comments: comments.value,
        // Anchor roles (PD-1258). Undefined while the feature is off / the user
        // can't govern, so the holder leaves the existing state alone.
        isAnchor: canAnchorSave.value ? anchorIntent.value : undefined,
        anchorRationale: canAnchorSave.value ? (anchorNote.value.trim() || null) : undefined,
      })
    }

    function validate() {
      saveAttempted.value = true
      if (suggestedLevel.value !== selectedLevel.value && !comments.value)
        return;

      save()
      editingComments.value = false
      saveAttempted.value = false
    }

    // Find weighting for a sub-factor
    function findSubFactorWeight(factorId, subFactorId) {
      const factor = props.factors.find(f => f.id === factorId)
      if (!factor) return 0
      const subFactor = factor.subFactors.find(sf => sf.id === subFactorId)
      return Number(subFactor?.weighting) ?? 0
    }

    // Find factor weight
    function findFactorWeight(factorId) {
      const factor = props.factors.find(f => f.id === factorId)
      if (!factor) return 0
      return Number(factor?.weighting) ?? 0
    }

    // Sub-factor weighted score:
    // ((score / maxLevelScore) ^ factor.curve_factor) * subFactor.weighting * factor.weighting * 1000
    function getSelectedLevel(answer) {
      const factor = props.factors.find(f => f.id === answer.factorId)
      if (!factor) return null
      const subFactor = factor.subFactors.find(sf => sf.id === answer.subFactorId)
      if (!subFactor) return null
      return subFactor.levels?.find(l => l.number === answer.selectedLevel) ?? null
    }

    function getSelectedLevelScore(answer) {
      return Number(getSelectedLevel(answer)?.score) || 0
    }

    function getSelectedLevelText(answer) {
      return getSelectedLevel(answer)?.name ?? ''
    }

    function getSubFactorScore(answer) {
      const factor = props.factors.find(f => f.id === answer.factorId)
      if (!factor) return 0
      const subFactor = factor.subFactors.find(sf => sf.id === answer.subFactorId)
      if (!subFactor) return 0
      const levels = subFactor.levels
      if (!levels || levels.length === 0) return 0
      const selectedLevel = levels.find(l => l.number === answer.selectedLevel)
      const score = Number(selectedLevel?.score) || 0
      const maxLevelScore = Math.max(...levels.map(l => Number(l.score) || 0))
      if (maxLevelScore === 0) return 0
      const subFactorWeight = Number(subFactor.weighting) || 0
      const factorWeight = Number(factor.weighting) || 0
      const curveFactor = Number(factor.curve_factor) || 1

      return Math.round(((score / maxLevelScore) ** curveFactor) * subFactorWeight * factorWeight * 1000)
    }

    // Factor score is the sum of its sub-factor weighted scores
    // (sub-factor and factor weightings are already baked into each contribution).
    function calculateFactorScore(factorAnswers) {
      return factorAnswers.reduce((sum, a) => sum + getSubFactorScore(a), 0)
    }

    // Maximum possible weighted score for a factor: factor.weighting * 1000
    // (assuming sub-factor weightings within the factor sum to 1).
    function getFactorMaxScore(factor) {
      return (Number(factor?.weighting) || 0) * 1000
    }

    const groupedAnswers = computed(() => {
      const groups = {}

      for (const answer of props.answers) {
        if (!groups[answer.factorId]) {
          const factor = props.factors.find(f => f.id === answer.factorId)
          if (factor) {
            groups[answer.factorId] = { factor, answers: [], factorScore: 0 }
          }
        }
        groups[answer.factorId]?.answers.push(answer)
      }

      // Calculate factor scores
      for (const group of Object.values(groups)) {
        group.factorScore = calculateFactorScore(group.answers, group.factor)
      }

      return Object.values(groups)
    })

    // The largest per-factor maximum across all factors — used to draw the factor
    // bars on a shared points scale (a track is full width only if its factor has
    // the highest max; lower-max factors get a proportionally shorter track).
    const maxFactorMaxScore = computed(() =>
      groupedAnswers.value.reduce((mx, group) => Math.max(mx, getFactorMaxScore(group.factor)), 0)
    )
    function factorTrackWidth(factor) {
      const mx = maxFactorMaxScore.value
      if (mx <= 0) return 100
      const maxScore = getFactorMaxScore(factor)
      return maxScore === 0 ? 100 : (maxScore / mx) * 100
    }

    // Total weighted score is the sum of factor scores (max ~1000 when weightings are normalised).
    const totalScore = computed(() => {
      if (isClassification.value)
        return null
      
      return Math.round(groupedAnswers.value.reduce((sum, group) => sum + group.factorScore, 0))
    })

    const trackLevels = computed(() => {
      return props.functionTrackLevels
        ?.filter(ftl => ftl.trackId === props.selectedTrack.id
          && ftl.functionId === props.selectedFunction.id)
        ?.map(ftl => ({ ...ftl, level: Number(ftl.level) }))
        ?.sort(function(a, b){return a.level - b.level})
        || [ { level: 0 }, { level: 21 } ];
    })

    // Pick the track level whose [evaluation_score_minimum, evaluation_score_maximum]
    // range contains the total score. Clamp to the first/last level when outside any range.
    const jobLevel = computed(() => {
      const score = totalScore.value
      const sorted = trackLevels.value
      if (sorted.length === 0) return 0

      const match = sorted.find(tl => {
        const lo = Number(tl.evaluation_score_minimum)
        const hi = Number(tl.evaluation_score_maximum)
        return score >= lo && score <= hi
      })
      if (match) return match.level

      if (score < Number(sorted[0].evaluation_score_minimum)) return sorted[0].level
      return sorted[sorted.length - 1].level
    })

    /* ---- Anchor roles (PD-1258) ----------------------------------------------
       Two independent things on this screen:
         1. "Save as an anchor role" — only on a live, editable FULL evaluation,
            for a user who may govern anchors. A classification can never be one.
         2. The at/above-ceiling advisory. Deliberately NOT wired into the save
            button's :disabled (unlike scoreOutOfRange, which is a hard block) —
            it flags for review, it does not prevent sign-off. */
    const anchorIntent = ref(!!props.evaluation.isAnchor)
    const anchorNote = ref(props.evaluation.anchorRationale || '')
    watch(() => props.evaluation.isAnchor, (v) => { anchorIntent.value = !!v })
    watch(() => props.evaluation.anchorRationale, (v) => { anchorNote.value = v || '' })

    const anchorDraftOpen = ref(false)
    /** Pill state from the shared model, so beta reads identical copy. */
    const anchorPill = computed(() => anchorDraftPill({
      on: anchorIntent.value, note: anchorNote.value, family: null,
    }))
    /* What the modal edits in draft mode: intent + rationale on the DRAFT, applied
       when the evaluation saves. `set` is the only way to turn the intent ON, which is
       how the rationale requirement is enforced. */
    const anchorDraftCtx = computed(() => ({
      on: anchorIntent.value,
      note: anchorNote.value,
      set: (n) => { anchorIntent.value = true; anchorNote.value = n; save() },
      clear: () => { anchorIntent.value = false; anchorNote.value = ''; save() },
    }))

    const canAnchorSave = computed(() =>
      props.canAnchor && !props.readOnly && !isClassification.value)

    /* ---- record-level governance bar (PD-1258) ----------------------------
       Setting or removing an anchor from the role's own levelling record, once the
       evaluation is COMPLETE. The draft summary has its own "Save as an anchor
       role" checkbox; after that it disappears, and without this the only route
       left is the control centre. Routes through the same CcAnchorModal as the row
       menu, so the explain-and-confirm contract is identical either way. */
    const recordAnchorAsk = ref(null)
    const recordAnchorInfo = computed(() =>
      props.anchors && props.docId != null ? props.anchors.anchorInfo(props.docId) : null)
    const recordIsAnchor = computed(() => !!recordAnchorInfo.value)
    const recordIsCeiling = computed(() => !!(recordAnchorInfo.value && recordAnchorInfo.value.isCeiling))
    /* Anchor governance is NOT the document's approval state. The server's
       canGovernAnchors checks only the levelling_anchor_roles setting + the caller's
       permission group — it never looks at the lifecycle. Gating this bar on the
       document's readOnly conflated the two, so a role awaiting approval showed no
       sign of being an anchor and offered no way to remove it, even to someone who
       governs anchors. The anchor belongs to the ROLE, and survives re-levelling.
    
       So: the bar RENDERS whenever the role is an anchor (information — a fact about
       the record, which a viewer should see), and additionally when the user may
       govern anchors (so a non-anchor can still be marked). The buttons themselves
       are what canGovern gates, inside CcAnchorModal. */
    const showRecordAnchorBar = computed(() =>
      !!props.anchors && (props.canAnchor || recordIsAnchor.value) &&
      props.evaluation.status === 'COMPLETED' && !isClassification.value)
    /* The counterpart to the anchor pills (PD-1258). Those appear on an EVALUATION
       record; this appears where they can't — a classification — so the summary
       explains why it cannot be anchored instead of going quiet about it. */
    const showRecordAnchorRoute = computed(() =>
      !!props.anchors && props.canAnchor && !recordIsAnchor.value &&
      props.evaluation.status === 'COMPLETED' && isClassification.value)

    /* The prototype's `showCeo`: the ceiling layers on an anchor, and once set it
       shows only on the holder — reassigning means removing it there first. */
    const showRecordCeilingPill = computed(() =>
      recordIsAnchor.value && (recordIsCeiling.value || !props.ceiling))
    /** CcAnchorModal expects a control-centre row; the record has the same facts. */
    const recordAnchorRole = computed(() => ({
      doc_id: props.docId,
      doc_name: props.docName,
      levelling: {
        method: 'evaluate',
        evaluation_status: props.evaluation.status,
        level_code: selectedTrackLevel.value && selectedTrackLevel.value.abbreviation,
      },
    }))

    // The outcome's framework position, resolved through the selected level row —
    // the level NAME is a client string and isn't comparable across functions.
    const selectedLevelPosition = computed(() => {
      const row = trackLevels.value.find(tl => Number(tl.level) === Number(selectedLevel.value))
      const pos = row && row.levelPosition != null ? Number(row.levelPosition) : null
      return pos != null && !Number.isNaN(pos) ? pos : null
    })

    /* Delegates to the SHARED rule rather than re-deriving it. The server decides
       the persisted flag independently (CEILING_FLAG_SQL), so this advisory has to
       agree with it; three hand-rolled copies of "at or above the ceiling" — here,
       in beta and in the shared model — could drift apart silently, and only the
       shared one carries tests. It self-excludes the ceiling holder by doc_id, so
       the CEO's own evaluation isn't perpetually flagged against itself. */
    const aboveCeiling = computed(() =>
      props.anchors.isAtOrAboveCeiling(selectedLevelPosition.value, props.docId))

    function toggleAnchorIntent(on) {
      anchorIntent.value = !!on
      if (!on) anchorNote.value = ''
      save()
    }

    // Returns 'below' / 'above' if the total score falls outside the track's
    // overall [min, max] evaluation range, otherwise false. Used to surface a
    // warning that the wrong track may be selected.
    const scoreOutOfRange = computed(() => {
      if (isClassification.value) return false
      const sorted = trackLevels.value
      if (!sorted || sorted.length === 0) return false
      const min = Number(sorted[0].evaluation_score_minimum)
      const max = Number(sorted[sorted.length - 1].evaluation_score_maximum)
      if (Number.isNaN(min) || Number.isNaN(max)) return false
      if (totalScore.value < min) return 'below'
      if (totalScore.value > max) return 'above'
      return false
    })

    // The level within a (discipline, track) pairing whose score band contains the
    // score, or null when the score falls outside every band for that pairing
    // (including when the pairing has no levels defined at all).
    function levelInBands(functionId, trackId, score) {
      return (props.functionTrackLevels ?? [])
        .filter(ftl => ftl.trackId === trackId && ftl.functionId === functionId)
        .map(ftl => ({ ...ftl, level: Number(ftl.level) }))
        .sort((a, b) => a.level - b.level)
        .find(tl =>
          score >= Number(tl.evaluation_score_minimum) && score <= Number(tl.evaluation_score_maximum)
        ) ?? null
    }

    // Every track (other than the current one) whose level bands actually
    // contain the total score — i.e. the score maps to a real level within the
    // track. Surfaced as switch-to options when the score is out of range for
    // the selected track, so the user can pick whichever track best describes
    // the role rather than being pushed to a single suggestion. Ordered by
    // proximity to the current track (on a tie, the out-of-range side first).
    const candidateTracks = computed(() => {
      if (!scoreOutOfRange.value) return []
      const fnId = props.selectedFunction?.id
      if (!fnId || !props.tracks?.length) return []

      const score = totalScore.value
      const currentPos = props.tracks.findIndex(t => t.id === props.selectedTrack?.id)

      const candidates = props.tracks
        .map((track, pos) => {
          // The specific level within this track that the score maps to; if the
          // score falls in no band, the track isn't a candidate.
          const level = levelInBands(fnId, track.id, score)
          return level ? { track, pos, level } : null
        })
        .filter(Boolean)
        .filter(c => c.track.id !== props.selectedTrack?.id)

      // Nearest by position; on a tie prefer the track on the out-of-range side.
      const dir = scoreOutOfRange.value === 'above' ? 1 : -1
      candidates.sort((a, b) => {
        const da = Math.abs(a.pos - currentPos)
        const db = Math.abs(b.pos - currentPos)
        if (da !== db) return da - db
        const aSide = Math.sign(a.pos - currentPos) === dir ? 0 : 1
        const bSide = Math.sign(b.pos - currentPos) === dir ? 0 : 1
        return aSide - bSide
      })

      return candidates
    })

    // Fallback when no track within the current discipline can accommodate the
    // score: the discipline itself is likely wrong, so look across the other
    // disciplines for one whose bands do contain it. Each candidate carries the
    // career track that makes it fit — the currently selected track wherever it
    // works, so switching discipline changes as little as possible. Ordered by
    // proximity to the current discipline.
    const candidateFunctions = computed(() => {
      if (!scoreOutOfRange.value) return []
      // A track switch within the current discipline is the smaller change, so it
      // takes precedence whenever one is available.
      if (candidateTracks.value.length) return []
      if (!props.functions?.length || !props.tracks?.length) return []

      const score = totalScore.value
      const currentPos = props.functions.findIndex(f => f.id === props.selectedFunction?.id)

      const candidates = props.functions
        .map((fn, pos) => {
          if (fn.id === props.selectedFunction?.id) return null
          const matches = props.tracks
            .map(track => {
              const level = levelInBands(fn.id, track.id, score)
              return level ? { track, level } : null
            })
            .filter(Boolean)
          if (!matches.length) return null
          // Keep the current career track where it works in this discipline;
          // otherwise fall back to the first track that does.
          const match = matches.find(m => m.track.id === props.selectedTrack?.id) ?? matches[0]
          return { fn, pos, track: match.track, level: match.level }
        })
        .filter(Boolean)

      candidates.sort((a, b) => Math.abs(a.pos - currentPos) - Math.abs(b.pos - currentPos))

      return candidates
    })

    // Material symbol for a track's switch-to card, inferred from its name:
    // executive-style tracks get a badge, people-management tracks a group, and
    // everything else (individual contributor / specialist) a single person.
    function trackIcon(track) {
      const name = (track?.name || '').toLowerCase()
      if (name.includes('exec')) return 'workspace_premium'
      if (name.includes('manage')) return 'groups'
      return 'person'
    }

    // Tint a track's switch-to card with its own brand colour: a solid shade for
    // the border/icon and a translucent wash behind the icon. Falls back to the
    // theme accent (via the CSS var defaults) when the track has no colour.
    function trackTintStyle(track) {
      const color = track?.backgroundColour
      if (!color) return {}
      return {
        '--tp-tint': utils.shadeColor(color, 20, -40),
        '--tp-bg': `color-mix(in srgb, ${color} 60%, transparent)`,
        '--tp-border': `color-mix(in srgb, ${color} 80%, var(--v3-border))`,
      }
    }

    const trackAbbreviation = computed(() => {
      return props.selectedTrack.abbreviation 
        ||  props.selectedTrack.name.split(' ')
              .map(word => word.substring(0, 1).toUpperCase())
              .join('')
        || 'L'
    })

    // Format job level as M1-M21
    const formattedJobLevel = computed(() => {
      const functionTrackLevel = trackLevels.value?.find(ftl => ftl.level === suggestedLevel.value)
      if (functionTrackLevel && functionTrackLevel.abbreviation) return functionTrackLevel.abbreviation

      return `${trackAbbreviation.value}${suggestedLevel.value}`
    })

    // The track-level record for the suggested level — carries its name,
    // description and score band, surfaced on the "Suggested level" tile.
    const suggestedLevelObj = computed(() =>
      trackLevels.value?.find(ftl => ftl.level === suggestedLevel.value) ?? null
    )

    // "min–max" band that maps to the suggested level (standard flow only —
    // classification has no scores). Uses an en dash to match the design.
    const suggestedLevelScoreRange = computed(() => {
      const tl = suggestedLevelObj.value
      if (!tl) return null
      const min = Number(tl.evaluation_score_minimum)
      const max = Number(tl.evaluation_score_maximum)
      if (Number.isNaN(min) || Number.isNaN(max)) return null
      return `${min}–${max}`
    })

    // Popover toggle for the level name/description.
    const showLevelInfo = ref(false)

    // Classification flow: map factor names to selected levels
    const classificationFactorLevels = computed(() => {
      if (!isClassification.value) return []
      return props.classificationAnswers.map(ca => {
        const factor = props.factors.find(f => f.name === ca.factorName)
        const factorWeight = factor ? findFactorWeight(factor.id) : 0
        const selectedLevel = trackLevels.value.find(tl => tl.level == ca.selectedLevel) ?? {
          level: ca.selectedLevel,
          abbreviation: `${trackAbbreviation.value}${ca.selectedLevel}`
        }
        return {
          factorId: factor?.id,
          factorName: ca.factorName,
          comments: ca.comments,
          rationaleProvenance: ca.rationaleProvenance || null,
          displayName: factor?.name || ca.factorName,
          backgroundColour: factor?.backgroundColour,
          selectedLevel: {
            ...selectedLevel,
            trackLevel: selectedLevel.abbreviation?.match(/\d+/)[0] ?? selectedLevel.level,
          },
          weight: factorWeight
        }
      })
    })

    // Row keys of classification factors that carry a justification — the only
    // rows the expand/collapse-all control acts on.
    function classificationCommentKeys() {
      return classificationFactorLevels.value
        .map((fl, fi) => (fl.comments ? `clf${fi}` : null))
        .filter(Boolean)
    }

    const hasClassificationComments = computed(() =>
      classificationFactorLevels.value.some(fl => fl.comments)
    )

    const allClassificationExpanded = computed(() => {
      const keys = classificationCommentKeys()
      return keys.length > 0 && keys.every(key => expandedFactors.value[key])
    })

    function toggleClassificationExpandAll() {
      const next = {}
      if (!allClassificationExpanded.value)
        classificationCommentKeys().forEach(key => { next[key] = true })
      expandedFactors.value = next
    }

    function trackLevelAbbreviation(level) {
      return trackLevels.value.find(tl => tl.level == level)?.abbreviation
    }

    // Classification flow: weighted average with disagreement handling
    const classificiationRecommendedLevel = computed(() => {
      if (!isClassification.value || classificationFactorLevels.value.length === 0) return 0
      let weightedSum = 0
      let totalWeight = 0
      for (const fl of classificationFactorLevels.value) {
        weightedSum += fl.selectedLevel.level * fl.weight
        totalWeight += fl.weight
      }
      if (totalWeight === 0) {
        // Fallback: simple average
        const sum = classificationFactorLevels.value.reduce((s, fl) => s + fl.selectedLevel.level, 0)
        return Math.round(sum / classificationFactorLevels.value.length)
      }

      const initialRecommendedLevel = Math.round(weightedSum / totalWeight)

      // Disagreement rules: stabilise if factors diverge significantly
      const levels = classificationFactorLevels.value
      const distances = levels.map(fl => ({
        ...fl,
        distance: Math.abs(fl.selectedLevel.level - initialRecommendedLevel)
      }))

      const countAt2 = distances.filter(d => d.distance === 2).length
      const anyAt3Plus = distances.some(d => d.distance >= 3)

      // Sort by weight descending to identify top 3
      const sortedByWeight = [...levels].sort((a, b) => b.weight - a.weight)
      const top3 = sortedByWeight.slice(0, 3)
      const top3Levels = top3.map(f => f.selectedLevel.level)
      const top3Span = Math.max(...top3Levels) - Math.min(...top3Levels)

      // A) Tight agreement: all within ±1
      const allWithin1 = distances.every(d => d.distance <= 1)
      if (allWithin1) return initialRecommendedLevel

      // B) Single mild outlier: exactly one at ±2, rest within ±1
      const restWithin1 = distances.filter(d => d.distance > 1).length <= 1
      if (countAt2 <= 1 && !anyAt3Plus && restWithin1) return initialRecommendedLevel

      // C) Material disagreement: stabilise to main cluster
      if (countAt2 > 1 || anyAt3Plus || top3Span >= 3) {
        // Find the main cluster among top 3 weighted factors
        // Use median of top 3 as cluster centre, then include factors within ±1 of it
        const sortedTop3Levels = [...top3Levels].sort((a, b) => a - b)
        const medianTop3 = sortedTop3Levels[1] // middle of 3

        // Collect all factor levels within ±1 of this cluster centre
        const clusterLevels = levels
          .filter(fl => Math.abs(fl.selectedLevel.level - medianTop3) <= 1)
          .map(fl => fl.selectedLevel.level)

        if (clusterLevels.length > 0) {
          // Weighted average within cluster
          let clusterWeightedSum = 0
          let clusterTotalWeight = 0
          for (const fl of levels) {
            if (Math.abs(fl.selectedLevel.level - medianTop3) <= 1) {
              clusterWeightedSum += fl.selectedLevel.level * fl.weight
              clusterTotalWeight += fl.weight
            }
          }
          const clusterLevel = clusterTotalWeight > 0
            ? clusterWeightedSum / clusterTotalWeight
            : clusterLevels.reduce((a, b) => a + b, 0) / clusterLevels.length

          // Tie-breaking: when between two adjacent levels, prefer the one
          // indicated by the highest-weighted factor
          const rounded = Math.round(clusterLevel)
          if (Math.abs(clusterLevel - rounded) === 0.5) {
            // Exact midpoint — prefer the level of the highest-weighted factor in cluster
            const highestWeightedInCluster = sortedByWeight
              .find(fl => Math.abs(fl.selectedLevel.level - medianTop3) <= 1)
            return highestWeightedInCluster ? highestWeightedInCluster.selectedLevel.level : rounded
          }
          return rounded
        }

        // Fallback: if no cluster found, use the top weighted factor's level
        return sortedByWeight[0].selectedLevel.level
      }

      return initialRecommendedLevel
    })

    const suggestedLevel = computed(() => {
      if (isClassification.value) {
        return classificiationRecommendedLevel.value
      } else {
        return jobLevel.value
      }
    })

    // Switching track shifts the level mapping, so the recommended level can
    // change while the summary stays mounted. Discard any manual adjustment and
    // persist the freshly suggested level.
    watch(suggestedLevel, () => {
      selectedLevel.value = suggestedLevel.value
      comments.value = ''
      editingComments.value = false
      save()
    })

    // Segmented control click: set the level and run the adjustment side-effects.
    function selectLevel(level) {
      selectedLevel.value = level
      
      // Adjusting the level via the toggle requires a justification, so open the
      // editor. Fired on user interaction only (not the programmatic mount set).
      if (selectedLevel.value !== suggestedLevel.value)
        editingComments.value = true
      else if (comments.value === '')
        editingComments.value = false
    }

    // Optional rationale (no level adjustment): revert to the saved value and
    // collapse back to the read-only view / "Add rationale" button.
    function cancelRationale() {
      comments.value = props.evaluation.comments || ''
      editingComments.value = false
    }

    function saveRationale() {
      save()
      editingComments.value = false
    }

    // Cancel an in-progress level adjustment: discard the change and its edits.
    function cancelAdjustment() {
      selectedLevel.value = suggestedLevel.value
      comments.value = props.evaluation.comments || ''
      editingComments.value = false
      saveAttempted.value = false
    }

    const adjustmentLevels = computed(() => {
      const sLevel = suggestedLevel.value

      return trackLevels.value.filter(ftl => ftl.level >= sLevel - 1 && ftl.level <= sLevel + 1)
    })

    const selectedTrackLevel = computed(() => {
      return trackLevels.value.find(tl => tl.level === selectedLevel.value)
    })

    const levelExplanation = computed(() => {
      if (isClassification.value) {
        const levels = classificationFactorLevels.value
        if (levels.length === 0) return { steps: [] }

        let weightedSum = 0
        let totalWeight = 0
        for (const fl of levels) {
          weightedSum += fl.selectedLevel.trackLevel * fl.weight
          totalWeight += fl.weight
        }

        const steps = []
        const factorDetails = levels.map(fl => ({
          ...fl,
          weight: Math.round(fl.weight * 100)
        }))
        steps.push({ label: 'Factor selections', factorDetails })

        if (totalWeight === 0) {
          const avg = levels.reduce((s, fl) => s + fl.selectedLevel.trackLevel, 0) / levels.length
          steps.push({ label: 'Simple average (no weights available)', value: avg.toFixed(2) })
          steps.push({ label: 'Recommended level', value: Math.round(avg), highlight: true })
          return { steps }
        }

        const initialLevel = Math.round(weightedSum / totalWeight)
        steps.push({ label: 'Weighted average', value: (weightedSum / totalWeight).toFixed(2) })
        steps.push({ label: 'Initial recommended level', value: initialLevel })

        const distances = levels.map(fl => Math.abs(fl.selectedLevel.trackLevel - initialLevel))
        const countAt2 = distances.filter(d => d === 2).length
        const anyAt3Plus = distances.some(d => d >= 3)
        const sortedByWeight = [...levels].sort((a, b) => b.weight - a.weight)
        const top3Levels = sortedByWeight.slice(0, 3).map(f => f.selectedLevel.trackLevel)
        const top3Span = Math.max(...top3Levels) - Math.min(...top3Levels)
        const allWithin1 = distances.every(d => d <= 1)
        const outliersOver1 = distances.filter(d => d > 1).length

        if (allWithin1) {
          steps.push({ label: 'Disagreement check', value: 'Tight agreement — all factors within ±1 level' })
        } else if (countAt2 <= 1 && !anyAt3Plus && outliersOver1 <= 1) {
          steps.push({ label: 'Disagreement check', value: 'Single mild outlier — one factor ±2 levels, no adjustment needed' })
        } else if (countAt2 > 1 || anyAt3Plus || top3Span >= 3) {
          const reasons = []
          if (countAt2 > 1) reasons.push(`${countAt2} factors are ±2 levels away`)
          if (anyAt3Plus) reasons.push('at least one factor is ±3+ levels away')
          if (top3Span >= 3) reasons.push(`top 3 weighted factors span ${top3Span} levels`)
          steps.push({ label: 'Disagreement check', value: `Material disagreement: ${reasons.join('; ')}` })
          steps.push({ label: 'Stabilisation', value: 'Level adjusted to the main cluster of the highest-weighted factors' })
        }

        steps.push({ label: 'Final recommended level', value: trackLevelAbbreviation(suggestedLevel.value), highlight: true })
        return { steps }
      } else {
        const steps = []
        steps.push({ label: 'Total score across all factors', value: `${totalScore.value}` })
        const rangeStep = {
          label: `Level chosen in range ${trackLevels.value[0].abbreviation} – ${trackLevels.value[trackLevels.value.length - 1].abbreviation}`,
          value: trackLevelAbbreviation(suggestedLevel.value),
        }
        if (scoreOutOfRange.value) {
          rangeStep.warning = `Score is ${scoreOutOfRange.value} the score range for the ${props.selectedTrack?.name} track in ${props.selectedFunction?.name} — a different track may be more appropriate.`
        }
        steps.push(rangeStep)
        steps.push({ label: 'Final recommended level', value: trackLevelAbbreviation(suggestedLevel.value), highlight: true })
        return { steps }
      }
    })

    // Condensed classification explanation for the tile popover: the value steps
    // only (weighted average, initial level, disagreement check, stabilisation) —
    // the factor-selections table and the final level are omitted, the latter
    // already being shown as the big code on the tile.
    const classificationExplanationSteps = computed(() =>
      levelExplanation.value.steps.filter(s => !s.factorDetails && !s.highlight)
    )

    // Popover toggle for the classification calculation breakdown.
    const showClassificationExplain = ref(false)

    const hasDerivations = computed(() => {
      return props.answers.some(a => a.derivation)
    })

    function getLevelColor(level) {
      const colors = ["grey", "green-grey", "blue-grey", "teal", "blue", "indigo", "deep-purple", "black"];
      return colors[level] || 'grey'
    }

    function getFactorColor(factor) {
      if (!isClassification.value && getFactorMaxScore(factor) === 0)
        return 'initial'

      if (!factor || !factor.backgroundColour)
        return '#F5A623'

      return utils.shadeColor(factor.backgroundColour, 20, -20)
    }

    function formatScore(score) {
      return `${Math.round(score)}%`
    }

    function formatScoreNumber(score) {
      return `${Math.round(score)}`
    }

    return {
      classificationExplanationSteps,
      showClassificationExplain,
      isClassification,
      isDraft,
      isStandardFlow,
      classificationFactorLevels,
      hasClassificationComments,
      allClassificationExpanded,
      toggleClassificationExpandAll,
      groupedAnswers,
      factorTrackWidth,
      expandedFactors,
      toggleFactorExpanded,
      expandAllStage,
      cycleExpandAll,
      groupHasComments,
      groupSubFactorItems,
      totalScore,
      jobLevel,
      formattedJobLevel,
      suggestedLevelObj,
      suggestedLevelScoreRange,
      showLevelInfo,
      hasDerivations,
      getLevelColor,
      getFactorColor,
      formatScore,
      formatScoreNumber,
      findFactorWeight,
      findSubFactorWeight,
      getSubFactorScore,
      getSelectedLevelScore,
      getSelectedLevelText,
      getFactorMaxScore,
      suggestedLevel,
      scoreOutOfRange,
      candidateTracks,
      candidateFunctions,
      trackIcon,
      trackTintStyle,
      adjustmentLevels,
      selectedLevel,
      selectedTrackLevel,
      comments,
      editingComments,
      // AI rationale provenance (PD-1301) on the per-answer justification rows
      isAtlasProvenance,
      provenanceLabel,
      updatedBy,
      updatedDate,
      selectLevel,
      cancelAdjustment,
      cancelRationale,
      saveRationale,
      validate,
      isDirty,
      // Anchor roles (PD-1258)
      anchorIntent,
      anchorNote,
      canAnchorSave,
      anchorDraftOpen,
      anchorPill,
      anchorDraftCtx,
      recordAnchorAsk,
      recordIsAnchor,
      recordIsCeiling,
      showRecordAnchorBar,
      showRecordAnchorRoute,
      showRecordCeilingPill,
      recordAnchorRole,
      aboveCeiling,
      toggleAnchorIntent,
      saveAttempted,
      save,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard">
      <div class="lvl-qcard-in">
        <!-- Header: title + Summary / Compare / Factor Breakdown toggle -->
        <div class="lvl-sum-head">
          <div class="lvl-sum-head-t">
            <div v-if="evaluation.status === 'DRAFT'" class="lvl-sum-eyebrow">Review and save</div>
            <h1 class="lvl-sum-title">{{ isClassification ? 'Classification' : 'Evaluation' }} summary
              <button
                type="button"
                class="lvl-iconbtn"
                @click.stop="$emit('openActivity')"
                title="Audit History"
              >
                <span class="ms">track_changes</span>
              </button>
            </h1>
          </div>
          <!-- The ONE anchor control on an in-journey summary (PD-1258), replacing the
               old checkbox + textarea. Everything explanatory — what an anchor is, the
               required rationale, how to back out — lives in the modal, so the summary
               stays about the evaluation. The flag is still applied on SAVE; the pill
               only edits draft state. -->
          <button
            v-if="canAnchorSave"
            type="button"
            class="lvl-anchor-pill"
            :class="{ 'is-on': anchorPill.on && !anchorPill.needs, 'is-needs': anchorPill.needs }"
            :title="anchorPill.tip"
            @click="anchorDraftOpen = true"
          >
            <span class="ms">anchor</span> {{ anchorPill.label }}
            <span class="ms lvl-anchor-pill-edit">{{ anchorPill.icon }}</span>
          </button>
          <div class="lvl-sum-tabs">
            <button type="button" class="on">
              <span class="ms">summarize</span> Summary
            </button>
            <button type="button" @click="$emit('compare')">
              <span class="ms">compare_arrows</span> Compare
            </button>
            <button type="button" @click="$emit('compare', 'breakdown')">
              <span class="ms">bar_chart</span> Factor Breakdown
            </button>
          </div>
        </div>

        <div class="lvl-sum-body">
          <!-- Anchor roles (PD-1258): the counterpart to the anchor pills. Those appear
               on an EVALUATION record; this appears where they can't — a classification
               — so the summary explains why it cannot be anchored instead of going
               quiet about it. Above the description, as in the prototype. -->
          <div v-if="showRecordAnchorRoute" class="lvl-anchor-route">
            <span class="ms">anchor</span>
            <span>Anchor roles need a full evaluation &mdash; a classification can&rsquo;t be a calibration reference.</span>
            <button type="button" class="lvl-anchor-route-act" @click="$emit('evaluateToAnchor')">
              Evaluate to anchor
            </button>
          </div>

          <!-- Description -->
          <p v-if="isClassification" class="lvl-sum-desc">
            This is a summary of your classification. All the scores from your answers have been brought
            together to create a weighted score which relates to a level in your framework.
          </p>
          <p v-else class="lvl-sum-desc">
            This is summary of your evaluation. All the scores from your
            answers have been brought together create a weighted
            score. This is then turned into a level.
          </p>

          <!-- Anchor roles (PD-1258): at/above the CEO ceiling. ADVISORY ONLY —
               it flags for review and is deliberately absent from the save
               button's :disabled, unlike scoreOutOfRange below. Neutral about
               which role is at fault. -->
          <div v-if="aboveCeiling" class="lvl-sum-warn lvl-anchor-ceilwarn">
            <div class="lvl-sum-warn-head">
              <span class="ms">anchor</span>
              <div class="lvl-sum-warn-copy">
                <div class="lvl-sum-warn-title">Flagged for review: at or above your framework ceiling</div>
                <div>
                  This outcome lands at or above the top of your framework —
                  <strong>{{ ceiling.title }}</strong> anchors it at
                  <strong>{{ ceiling.levelCode || 'its level' }}</strong>. It saves as normal, but check
                  this role’s answers <em>and</em> the ceiling role before sign-off — neither is assumed
                  wrong.
                </div>
              </div>
            </div>
          </div>

          <!-- Out-of-range track prompt -->
          <div v-if="scoreOutOfRange" class="lvl-sum-warn lvl-sum-warn--prompt">
            <div class="lvl-sum-warn-head">
              <span class="ms">swap_horiz</span>
              <div class="lvl-sum-warn-copy">
                <div class="lvl-sum-warn-title">
                  Pick the track that fits this score
                </div>
                <div>
                  The total score of <strong>{{ totalScore }}</strong> falls
                  {{ scoreOutOfRange === 'below' ? 'below' : 'above' }}
                  the score range defined for the
                  <strong>{{ selectedTrack?.name }}</strong> track in
                  <strong>{{ selectedFunction?.name }}</strong>.
                </div>
                <div v-if="candidateTracks.length && isDraft && !readOnly" class="lvl-sum-warn-actions">
                  <span>
                    This score maps to a level in
                    {{ candidateTracks.length === 1 ? 'another track' : `${candidateTracks.length} other tracks` }}.
                    Choose whichever track actually describes how this role works, or review your answers first.
                  </span>
                  <div class="lvl-trackpick">
                    <button
                      v-for="candidate in candidateTracks"
                      :key="candidate.track.id"
                      type="button"
                      class="lvl-trackpick-card"
                      :style="trackTintStyle(candidate.track)"
                      @click="$emit('changeTrack', candidate.track.id)"
                    >
                      <span class="lvl-trackpick-ic"><span class="ms">{{ trackIcon(candidate.track) }}</span></span>
                      <span class="lvl-trackpick-t">{{ candidate.track.name }}</span>
                      <span
                        v-if="candidate.track.description && candidate.track.description !== candidate.track.name"
                        class="lvl-trackpick-d"
                      >{{ candidate.track.description }}</span>
                      <b class="lvl-trackpick-d">Maps to Level {{ candidate.level.abbreviation }}</b>
                    </button>
                  </div>
                  <button
                    type="button"
                    class="lvl-btn lvl-btn--outlined"
                    @click="$emit('reviewAnswers')"
                  >
                    <span class="ms">edit</span> Review answers instead
                  </button>
                </div>
                <!-- No track within this discipline fits the score, so the
                     discipline itself is the likelier mismatch. -->
                <div v-else-if="candidateFunctions.length && isDraft && !readOnly" class="lvl-sum-warn-actions">
                  <span>
                    No career track in <strong>{{ selectedFunction?.name }}</strong> covers this score, but it does map
                    to a level in
                    {{ candidateFunctions.length === 1 ? 'another discipline' : `${candidateFunctions.length} other disciplines` }}.
                    Choose whichever discipline actually describes this role, or review your answers first.
                  </span>
                  <div class="lvl-trackpick">
                    <button
                      v-for="candidate in candidateFunctions"
                      :key="candidate.fn.id"
                      type="button"
                      class="lvl-trackpick-card"
                      @click="$emit('changeFunction', { functionId: candidate.fn.id, trackId: candidate.track.id })"
                    >
                      <span class="lvl-trackpick-ic"><span class="ms">category</span></span>
                      <span class="lvl-trackpick-t">{{ candidate.fn.name }}</span>
                      <span
                        v-if="candidate.fn.description && candidate.fn.description !== candidate.fn.name"
                        class="lvl-trackpick-d"
                      >{{ candidate.fn.description }}</span>
                      <b class="lvl-trackpick-d">
                        Maps to Level {{ candidate.level.abbreviation }}
                        <template v-if="candidate.track.id !== selectedTrack?.id">
                          on the {{ candidate.track.name }} track
                        </template>
                      </b>
                    </button>
                  </div>
                  <button
                    type="button"
                    class="lvl-btn lvl-btn--outlined"
                    @click="$emit('reviewAnswers')"
                  >
                    <span class="ms">edit</span> Review answers instead
                  </button>
                </div>
                <div v-else class="lvl-sum-warn-defs">
                  <div class="lvl-sum-warn-def">
                    This may indicate that a different track is more appropriate for this role.
                  </div>
                </div>
              </div>
            </div>
            
          </div>

          <!-- Metric tiles -->
          <div class="lvl-sum-metrics">
            <div v-if="!isClassification" class="lvl-metric lvl-metric--score">
              <div class="ml">Score</div>
              <div class="mv" data-testid="total-score">{{ totalScore }}</div>
            </div>
            <div class="lvl-metric lvl-metric--level">
              <div class="ml">
                <span>Suggested {{ isClassification ? 'Classification' : 'Level' }}</span>
                <v-menu
                  v-if="suggestedLevelObj && (suggestedLevelObj.name || suggestedLevelObj.description)"
                  v-model="showLevelInfo"
                  :close-on-content-click="false"
                  offset-y
                  max-width="320"
                  content-class="lvl-eval lvl-levelinfo-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="lvl-metric-info"
                      :class="{ 'is-active': showLevelInfo }"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="About this level"
                      title="About this level"
                    >
                      <span class="ms">info</span>
                    </button>
                  </template>
                  <div class="lvl-levelinfo">
                    <div class="lvl-levelinfo-h">
                      <template v-if="suggestedLevelObj.name">{{ suggestedLevelObj.name }}</template>
                    </div>
                    <p v-if="suggestedLevelObj.description" class="lvl-levelinfo-d">{{ suggestedLevelObj.description }}</p>
                  </div>
                </v-menu>

                <!-- Classification: how the suggestion was calculated (replaces the
                     old Explanation dialog with a condensed inline popover). -->
                <v-menu
                  v-if="isClassification && classificationExplanationSteps.length"
                  v-model="showClassificationExplain"
                  :close-on-content-click="false"
                  offset-y
                  max-width="360"
                  content-class="lvl-eval lvl-levelinfo-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="lvl-metric-info"
                      :class="{ 'is-active': showClassificationExplain }"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="How this classification was calculated"
                      title="How this classification was calculated"
                    >
                      <span class="ms">calculate</span>
                    </button>
                  </template>
                  <div class="lvl-levelinfo lvl-calcinfo">
                    <div class="lvl-levelinfo-h">How this classification was calculated</div>
                    <dl class="lvl-calcinfo-rows">
                      <div
                        v-for="(step, si) in classificationExplanationSteps"
                        :key="`cx${si}`"
                        class="lvl-calcinfo-row"
                      >
                        <dt>{{ step.label }}</dt>
                        <dd>{{ step.value }}</dd>
                      </div>
                    </dl>
                  </div>
                </v-menu>
              </div>
              <div class="mv">
                <span data-testid="suggested-level">{{ formattedJobLevel }}</span>
                <span
                  v-if="!isClassification && suggestedLevelScoreRange"
                  class="mv-note"
                >covers scores {{ suggestedLevelScoreRange }}</span>
              </div>
            </div>
            <div class="lvl-metric lvl-metric--track">
              <div class="ml">
                <span>Track</span>
                <button
                  v-if="isDraft && isStandardFlow && canEditTrack"
                  type="button"
                  :disabled="readOnly"
                  aria-label="Edit track"
                  title="Edit track and function"
                  @click="$emit('editTrack')"
                >
                  <span class="ms">edit</span>
                </button>
              </div>
              <div class="mv">{{ selectedTrack?.name ?? 'Unkown' }}</div>
              <div class="msub">in {{ selectedFunction?.name ?? 'Unkown' }}</div>
            </div>
          </div>

          <!-- Anchor roles (PD-1258): the governance bar on the role's LEVELLING
               RECORD. The draft summary offers "Save as an anchor role" as part of
               saving; once the evaluation is complete that checkbox is gone, so this
               is how an anchor is set or removed after the fact without going back
               to the control centre. Mirrors the prototype's LvlAnchorBar. -->
          <div v-if="showRecordAnchorBar" class="lvl-anchor-chips">
            <button
              type="button"
              class="lvl-anchor-pill"
              :class="{ 'is-on': recordIsAnchor }"
              :title="recordIsAnchor
                ? 'Anchor role — click for details, to amend the rationale, or to remove it'
                : 'Not an anchor. Click to learn what anchor roles are and mark this one.'"
              @click="recordAnchorAsk = 'anchor'"
            >
              <span class="ms">anchor</span>
              Anchor role<template v-if="!recordIsAnchor"> · not set</template>
              <span class="ms lvl-anchor-pill-edit">edit</span>
            </button>
            <button
              v-if="showRecordCeilingPill"
              type="button"
              class="lvl-anchor-pill"
              :class="{ 'is-on': recordIsCeiling }"
              :title="recordIsCeiling
                ? 'This anchor fixes the top of the framework. Click for details or to remove it.'
                : 'No ceiling is set. Click to learn about the CEO ceiling and set it on this anchor.'"
              @click="recordAnchorAsk = 'ceo'"
            >
              <span class="ms">vertical_align_top</span>
              CEO ceiling<template v-if="!recordIsCeiling"> · not set</template>
              <span class="ms lvl-anchor-pill-edit">edit</span>
            </button>
          </div>

          <!-- Classification flow: factor levels -->
          <template v-if="isClassification">
            <div class="lvl-sum-scores-h">
              <h3>Factor levels</h3>
              <button
                v-if="hasClassificationComments"
                type="button"
                class="lvl-q-showall"
                @click="toggleClassificationExpandAll"
              >
                <span class="ms">{{ allClassificationExpanded ? 'unfold_less' : 'unfold_more' }}</span>
                {{ allClassificationExpanded ? 'Collapse all' : 'Expand all' }}
              </button>
            </div>

            <div class="lvl-bars">
              <div
                v-for="(fl, fi) in classificationFactorLevels"
                :key="`clf${fi}`"
                class="ev-factor"
                :class="{ 'is-open': expandedFactors[`clf${fi}`] }"
              >
                <div class="ev-factor-head" @click="toggleFactorExpanded(`clf${fi}`)">
                  <div class="lvl-bar">
                    <span class="lvl-bar-l">{{ fl.displayName }}</span>
                    <div class="lvl-bar-track">
                      <div
                        class="lvl-bar-fill"
                        :style="{ width: `${(fl.selectedLevel.level / 21) * 100}%`, backgroundColor: getFactorColor(fl) }"
                      ></div>
                    </div>
                    
                    <span class="lvl-bar-v"><span class="lvl-bar-max">{{ Math.round(fl.weight * 100) }}% · </span>{{ fl.selectedLevel.abbreviation }}</span>
                  </div>
                  <div class="ev-factor-act">
                    <button
                      type="button"
                      class="ev-factor-chev"
                      :class="{ 'is-hidden': !fl.comments }"
                      :aria-label="expandedFactors[`clf${fi}`] ? 'Hide comments' : 'Show comments'"
                      @click.stop="fl.comments && toggleFactorExpanded(`clf${fi}`)"
                    >
                      <span class="ms">{{ expandedFactors[`clf${fi}`] ? 'chat_bubble' : 'chat_bubble_outline' }}</span>
                    </button>
                    <button
                      v-if="isDraft"
                      type="button"
                      
                      :disabled="readOnly"
                      aria-label="Edit answer"
                      title="Edit this classification answer"
                      @click.stop="$emit('editClassificationFactor', { factorId: fl.factorId, factorName: fl.factorName })"
                    >
                      <span class="ms">edit</span>
                    </button>
                  </div>
                </div>
                <v-expand-transition>
                  <div v-if="fl.comments && expandedFactors[`clf${fi}`]" class="lvl-sub-ratrow">
                    <span class="ms" :class="{ 'is-atlas': isAtlasProvenance(fl.rationaleProvenance) }">{{ isAtlasProvenance(fl.rationaleProvenance) ? 'explore' : 'chat_bubble' }}</span>
                    <div class="lvl-sub-rattxt ev-just-txt">{{ fl.comments }}<span v-if="provenanceLabel(fl.rationaleProvenance, { short: true })" class="lvl-sub-ratby">{{ provenanceLabel(fl.rationaleProvenance, { short: true }) }}</span></div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </template>

          <!-- Standard/Guided flow: factor scores -->
          <template v-else>
            <div class="lvl-sum-scores-h">
              <h3>Role evaluation summary</h3>
              <button type="button" class="lvl-q-showall" @click="cycleExpandAll">
                <span class="ms">{{ expandAllStage ? 'unfold_less' : 'unfold_more' }}</span>
                {{ expandAllStage === 'collapsed' ? 'Expand all' : 'Collapse all' }}
              </button>
            </div>

            <div class="lvl-bars">
              <div
                v-for="(group, gi) in groupedAnswers"
                :key="`factor${gi}`"
                class="ev-factor"
                :class="{ 'is-open': expandedFactors[`factor${gi}`] }"
              >
                <div class="ev-factor-head" @click="toggleFactorExpanded(`factor${gi}`)">
                  <div class="lvl-bar">
                    <span class="lvl-bar-l">{{ group.factor.name }}</span>
                    <div class="lvl-bar-track" :style="{ width: `${factorTrackWidth(group.factor)}%` }">
                      <div
                        :class="{ 'lvl-sbar-fill': true, 'lvl-sbar-fill--hatch': getFactorMaxScore(group.factor) === 0 }"
                        :style="{ width: `${getFactorMaxScore(group.factor) > 0 ? Math.min(100, (group.factorScore / getFactorMaxScore(group.factor)) * 100) : 100}%`, backgroundColor: getFactorColor(group.factor) }"
                      ></div>
                    </div>
                    <span class="lvl-bar-v">
                      <template v-if="getFactorMaxScore(group.factor) > 0">
                        {{ formatScoreNumber(group.factorScore) }}<span class="lvl-bar-max"> / {{ formatScoreNumber(getFactorMaxScore(group.factor)) }}</span>
                      </template>
                      <span v-else class="lvl-bar-max">Informational</span>
                    </span>
                  </div>
                  <div class="ev-factor-act">
                    <span
                      class="ms ev-factor-note"
                      :class="{ 'is-hidden': !groupHasComments(group) }"
                      title="One or more sub-factors has a justification"
                    >chat_bubble_outline</span>
                    <button
                      type="button"
                      class="ev-factor-chev"
                      :aria-label="expandedFactors[`factor${gi}`] ? 'Collapse' : 'Expand'"
                      @click.stop="toggleFactorExpanded(`factor${gi}`)"
                    >
                      <span class="ms">expand_more</span>
                    </button>
                  </div>
                </div>
                <v-expand-transition>
                  <div v-if="expandedFactors[`factor${gi}`]" class="ev-subs">
                    <div
                      v-for="(item, ci) in groupSubFactorItems(group)"
                      :key="`subfactor${gi}-${ci}`"
                      class="lvl-subitem"
                    >
                      <div
                        class="lvl-sub-row ev-sub-row"
                        :class="{ 'is-clickable': item.text }"
                        @click="item.text && toggleFactorExpanded(`factor${gi}-sf${ci}`)"
                      >
                        <div class="lvl-sub-name">{{ item.label }}</div>
                        <div class="lvl-sub-lvl">
                          <span class="lvl-sub-chip" :style="{ '--sub-tint': getFactorColor(group.factor) }">{{ item.levelScore }}</span>
                          <span class="lvl-sub-meta ev-sub-lvltext">{{ item.levelText }}</span>
                        </div>
                        <div class="lvl-sub-pts">{{ item.score ? formatScoreNumber(item.score) : '' }}</div>
                        <div class="lvl-sub-act">
                          <button
                            v-if="item.text"
                            type="button"
                            :aria-label="expandedFactors[`factor${gi}-sf${ci}`] ? 'Hide justification' : 'Show justification'"
                            @click.stop="toggleFactorExpanded(`factor${gi}-sf${ci}`)"
                          >
                            <span class="ms">{{ expandedFactors[`factor${gi}-sf${ci}`] ? 'chat_bubble' : 'chat_bubble_outline' }}</span>
                          </button>
                          <button
                            v-if="isDraft && isStandardFlow"
                            type="button"
                            :disabled="readOnly"
                            aria-label="Edit answer"
                            title="Edit this sub-factor answer"
                            @click.stop="$emit('editSubFactor', { factorId: item.factorId, subFactorId: item.subFactorId })"
                          >
                            <span class="ms">edit</span>
                          </button>
                        </div>
                      </div>
                      <v-expand-transition>
                        <div v-if="item.text && expandedFactors[`factor${gi}-sf${ci}`]" class="lvl-sub-ratrow">
                          <span class="ms" :class="{ 'is-atlas': isAtlasProvenance(item.provenance) }">{{ isAtlasProvenance(item.provenance) ? 'explore' : 'format_quote' }}</span>
                          <div class="lvl-sub-rattxt">
                            <div class="ev-just-lbl">Justification<span v-if="provenanceLabel(item.provenance, { short: true })" class="lvl-sub-ratby">{{ provenanceLabel(item.provenance, { short: true }) }}</span></div>
                            <div class="ev-just-txt">{{ item.text }}</div>
                          </div>
                        </div>
                      </v-expand-transition>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </template>

          <!-- Read-only adjustment / rationale -->
          <template v-if="readOnly || evaluation.status !== 'DRAFT'">
            <div v-if="selectedLevel !== suggestedLevel || comments" class="lvl-adjust">
              <template v-if="selectedLevel !== suggestedLevel">
                <div class="lvl-adjust-h">Adjusted level</div>
                <div class="ev-adjusted-lvl">Level {{ selectedTrackLevel?.abbreviation }}</div>
                <div class="lvl-adjust-h">Justification</div>
              </template>
              <div v-else class="lvl-adjust-h">Rationale</div>
              <div class="lvl-ro-rationale">{{ comments }}</div>
            </div>
          </template>

          <!-- Draft adjustment / rationale editor -->
          <div v-else class="lvl-adjust">
            <div class="lvl-adjust-h">Adjust level (optional)</div>

            <div class="lvl-seg" role="group" aria-label="Adjust level">
              <button
                v-for="l in adjustmentLevels"
                :key="l.level"
                type="button"
                :class="{ on: selectedLevel === l.level }"
                :aria-pressed="selectedLevel === l.level"
                @click="selectLevel(l.level)"
              >
                Level {{ l.abbreviation }}
              </button>
            </div>

            <br />

            <!-- Editing the justification (adjusted) or rationale (not adjusted) -->
            <template v-if="editingComments">
              <div class="lvl-adjust-rationale">
                <v-textarea class="lvl-textarea"
                  v-model="comments"
                  placeholder="Explain your reasoning or add context..."
                  outlined
                  hide-details
                  auto-grow
                  rows="4"
                  :error="!!saveAttempted && selectedLevel !== suggestedLevel && !comments ? 'Required' : null"
                  >
                </v-textarea>
              </div>
              <div v-if="selectedLevel === suggestedLevel" class="lvl-orat-hint">
                Optional — capturing your reasoning helps with calibration, future reference and audit.
              </div>
              <div v-if="selectedLevel !== suggestedLevel" class="lvl-adjust-btns">
                <button type="button" class="lvl-btn lvl-btn--outlined" @click="cancelAdjustment">Cancel</button>
                <button type="button" class="lvl-btn" @click="validate" :disabled="!isDirty">Change Level</button>
              </div>
              <div v-else class="lvl-adjust-btns">
                <button type="button" class="lvl-btn lvl-btn--outlined" @click="cancelRationale">Cancel</button>
                <button type="button" class="lvl-btn" @click="saveRationale" :disabled="!isDirty">Save rationale</button>
              </div>
            </template>

            <!-- Existing justification / rationale, shown read-only with edit affordance -->
            <div v-else-if="comments" class="lvl-q-rationale-view ev-rationale-view">
              <h5>
                <span>{{ selectedLevel !== suggestedLevel ? 'Justification' : 'Rationale' }}</span>
                <button type="button" aria-label="Edit" @click="editingComments = true"><span class="ms">edit</span></button>
              </h5>
              <div class="rtxt">{{ comments }}</div>
              <div class="ev-rationale-meta">
                <span>{{ updatedBy }}</span><span v-if="updatedDate"> · {{ updatedDate }}</span>
              </div>
            </div>

          </div>
          <div>
            <!-- No rationale captured yet -->
            <button
              v-if="!readOnly && evaluation.status === 'DRAFT' && !editingComments && !comments"
              type="button"
              class="lvl-orat-add"
              @click="editingComments = true"
            >
              <span class="ms">edit</span> Add overall rationale (optional)
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="lvl-cardfoot">
        <span class="restart">
          <button
            type="button"
            class="lvl-btn lvl-btn--outlined"
            :disabled="readOnly"
            @click="$emit('restart')"
          >
            Re-evaluate
          </button>
          <button
            v-if="canDelete"
            type="button"
            class="lvl-btn lvl-btn--danger"
            :disabled="readOnly"
            @click="$emit('delete')"
          >
            Delete
          </button>
        </span>
        <span v-if="evaluation.status === 'DRAFT' && !readOnly" class="prog" :class="{ 'prog--warn': scoreOutOfRange }">
          {{ scoreOutOfRange
            ? 'Switch to a track that fits this score, or review your answers, before you can save.'
            : 'Review the scores above, then save your evaluation.' }}
        </span>
        <span v-else class="prog"></span>
        <span class="nav">
          <!-- A viewer's answers can't be saved, so completing is hidden rather
               than offered disabled -->
          <button
            v-if="evaluation.status === 'DRAFT' && !readOnly"
            type="button"
            class="lvl-btn"
            :disabled="!!scoreOutOfRange"
            @click="$emit('complete')"
          >
            <span class="ms">check</span> Accept Level &amp; Save
          </button>
        </span>
      </div>
    </div>

    <!-- Anchor / CEO-ceiling explain-and-confirm, opened from the record's
         governance bar. Same component the control-centre row menu uses. -->
    <!-- Draft (in-journey) anchor modal: the pill's confirm surface. Writes nothing —
         it edits the draft, and the evaluation save applies the flag. -->
    <CcAnchorModal
      v-if="anchorDraftOpen"
      :role="recordAnchorRole"
      kind="anchor"
      :anchors="anchors"
      :draft-ctx="anchorDraftCtx"
      @close="anchorDraftOpen = false"
    />

    <CcAnchorModal
      v-if="recordAnchorAsk"
      :role="recordAnchorRole"
      :kind="recordAnchorAsk"
      :anchors="anchors"
      @close="recordAnchorAsk = null"
      @saved="recordAnchorAsk = null"
    />
  </div>

</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped>
/* Summary card gets a little more breathing room than the question shell. */
.lvl-qcard-in { padding: 28px 32px; }

/* Full-page evaluation (.eval-docked): the nav is sticky at the top and the
   action footer is fixed to the viewport bottom (with a reserved 96px gutter).
   Cap the card to the band between them and let ONLY the body scroll, so the
   header — and its Summary / Compare / Factor Breakdown tabs — stays visible.
   Offset = main padding-top (24) + sticky nav (~90) + reserved footer (72). */
.eval-docked .lvl-qcard { max-height: calc(100dvh - 186px); }
.eval-docked .lvl-qcard-in { flex: 1 1 auto; min-height: 0; }
.eval-docked .lvl-sum-head { flex: 0 0 auto; }
.eval-docked .lvl-sum-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding-right: 10px;
  padding-bottom: 5px;
}

.lvl-sum-title { font-family: Georgia, 'Times New Roman', serif; }

/* Track metric: push the edit pencil to the far edge of the label row. */
.lvl-metric--track .ml { justify-content: space-between; }
.lvl-metric .ms { font-size: 14px; }

/* Section header explanation/expand toggle sits to the right. */
.lvl-sum-scores-h { margin-top: 4px; }

/* ---- factor rows: theme mini-bar + row-level expand / edit affordances ---- */
.lvl-bars { margin-bottom: 22px; }
.ev-factor { border-bottom: 1px solid var(--v3-border-soft); }
.ev-factor:last-child { border-bottom: 0; }
.ev-factor-head { display: flex; align-items: center; gap: 12px; padding: 10px 0; cursor: pointer; }
.ev-factor-head .lvl-bar { flex: 1 1 auto; min-width: 0; grid-template-columns: minmax(120px, 240px) 1fr auto; }
.ev-factor-head .lvl-bar-track { height: 14px; }
.ev-factor-head .lvl-bar-fill { opacity: 1; }
.ev-factor-head .lvl-bar-v { min-width: 84px; font-size: 14px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ev-factor-head .lvl-bar-max { color: var(--v3-text-3); font-size: 12px; font-weight: 600; }
.ev-factor-act { flex: 0 0 auto; display: flex; align-items: center; gap: 2px; }
.ev-factor-note { color: var(--v3-text-3); font-size: 18px; }
/* keep the note's space reserved so factor scores align whether or not a factor has a justification */
.ev-factor-note.is-hidden { visibility: hidden; }
.ev-factor-chev { width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
  border: 0; border-radius: var(--v3-r-sm); background: transparent; color: var(--v3-text-3); cursor: pointer; }
.ev-factor-chev:hover { background: var(--v3-surface-2); color: var(--v3-text); }
/* keep the justification toggle's space reserved so the weighting + level line up
   whether or not a factor has a justification */
.ev-factor-chev.is-hidden { visibility: hidden; pointer-events: none; }
.ev-factor-chev .ms { transition: transform .15s; }
.ev-factor.is-open .ev-factor-chev .ms { transform: rotate(180deg); }

/* expanded sub-factors container */
.ev-subs { margin: 2px 0 12px 8px; border: 1px solid var(--v3-border); border-radius: 12px;
  background: var(--v3-surface-2); overflow: hidden; }
.ev-sub-row.is-clickable { cursor: pointer; }
.ev-sub-lvltext { margin-left: 8px; }
.ev-just-lbl { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: var(--v3-text-3); margin-bottom: 2px; }
.ev-just-txt { white-space: pre-wrap; line-height: 1.5; color: var(--v3-text); overflow-wrap: anywhere; }

/* sub-row action buttons match the theme's sub-act affordance */
.lvl-sub-act button { width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
  border: 0; border-radius: var(--v3-r-sm); background: transparent; color: var(--v3-text-3); cursor: pointer; }
.lvl-sub-act button:hover { background: var(--v3-surface); color: var(--v3-text); }
.lvl-sub-act button:disabled { opacity: .4; cursor: not-allowed; }

/* ---- read-only / draft rationale block ---- */
.ev-adjusted-lvl { font-weight: 700; color: var(--v3-text); margin-bottom: 12px; }
.ev-rationale-view { margin-top: 14px; align-self: stretch; }
.ev-rationale-meta { margin-top: 8px; font-size: 11px; color: var(--v3-text-3); }

.lvl-seg button:focus-visible { outline: 2px solid var(--v3-accent); outline-offset: 1px; }

.lvl-adjust-rationale { margin-top: 14px; }
.ev-textarea.v-textarea ::v-deep .v-input__control { min-height: 0; }
.ev-textarea.v-textarea ::v-deep fieldset { border-color: var(--v3-border); border-radius: var(--v3-r-md); }
.ev-textarea.v-textarea.v-input--is-focused ::v-deep fieldset { border-color: var(--v3-accent); }
.ev-textarea.v-textarea ::v-deep textarea { color: var(--v3-text); font-family: var(--font-sans); line-height: 1.5; }
.ev-textarea.v-textarea ::v-deep .v-text-field__slot { background: var(--v3-surface); border-radius: var(--v3-r-md); }

/* ---- score-breakdown dialog reskin (structure kept, tokens applied) ---- */
.breakdown-modal { border-radius: var(--v3-r-lg) !important; background: var(--v3-surface); }
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px !important; font-size: 17px; font-weight: 700; color: var(--v3-text);
  border-bottom: 1px solid var(--v3-border);
}
.modal-footer { padding: 14px 20px; border-top: 1px solid var(--v3-border); }

.factor-breakdown { border: 1px solid var(--v3-border); border-radius: var(--v3-r-md); overflow: hidden; }
.factor-breakdown ::v-deep .breakdown-section { border: 0; border-bottom: 1px solid var(--v3-border); }
.factor-breakdown ::v-deep .breakdown-section:last-child { border-bottom: 0; }
.factor-breakdown ::v-deep .breakdown-section::before { box-shadow: none !important; }
.breakdown-header { background: var(--v3-surface-2); padding: 0 12px; font-size: 13.5px; min-height: 40px !important; }
.breakdown-header ::v-deep .v-expansion-panel-header__content {
  display: flex; justify-content: space-between; align-items: center;
}
.breakdown-header-title { display: flex; align-items: center; gap: 6px; font-weight: 600; color: var(--v3-text); }
.breakdown-toggle-icon { color: var(--v3-text-3); font-size: 18px; }
.breakdown-score { margin-left: 5px; font-weight: 700; color: var(--v3-accent); }
.breakdown-table { font-size: 13px; }
.breakdown-table ::v-deep table { background: transparent; }
.breakdown-table ::v-deep th { color: var(--v3-text-2) !important; font-weight: 700; border-bottom: 1px solid var(--v3-border) !important; }
.breakdown-table ::v-deep td { color: var(--v3-text-2); border-bottom: 1px solid var(--v3-border-soft) !important; }
.subfactor-title { font-weight: 600; color: var(--v3-text); }
.ev-deriv-info { color: var(--v3-accent); font-size: 16px; margin-left: 4px; cursor: help; }

.level-info { margin: 12px 0 4px; }
.level-info-step { padding: 8px 0; border-bottom: 1px solid var(--v3-border-soft); }
.level-info-step:last-child { border-bottom: 0; }
.level-info-step--highlight { background: var(--success-bg); border-radius: var(--v3-r-md); padding: 12px; margin-top: 8px; }
.level-info-label { font-size: 13px; color: var(--v3-text-2); margin-bottom: 2px; }
.level-info-value { font-size: 15px; color: var(--v3-text); }
.level-info-value--bold { font-size: 18px; font-weight: 700; }
.level-info-warning {
  display: flex; align-items: flex-start; gap: 6px; margin-top: 6px; padding: 8px 10px;
  font-size: 12.5px; line-height: 1.45; color: var(--v3-warn); background: var(--v3-warn-bg); border-radius: var(--v3-r-sm);
}
.level-info-warning .ms { color: var(--v3-warn); flex: 0 0 auto; font-size: 18px; }
.derivation-tooltip { max-width: 300px; font-size: 12px; }
</style>
